import { useEffect, useMemo, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';

import { eventDoc, getEventId } from '../event/paths';
import { db } from '../lib/firebase';
import { normalizeDisabledMenuItemIds } from './visibility';
import { normalisePromoteBannerMode } from '../utils/ticketedEventsUi';

const CACHE_PREFIX = 'menuVisibilitySettings:';
const CACHE_VERSION = 'v1';

export const DEFAULT_MENU_VISIBILITY_SETTINGS = {
  specialEventsMenuEnabled: false,
  disabledMenuItemIds: [],
  // Dashboard switches (Brand dashboard). Missing fields keep today's behaviour:
  // attendance status shown, every menu item offered as a Quick Action, the
  // Promote & Earn banner for the brand's own events only.
  attendanceStatusEnabled: true,
  quickActionHiddenIds: [],
  promoteBannerMode: 'own'
};

function normalizeMenuVisibilitySettings(value) {
  const data = value && typeof value === 'object' ? value : {};
  return {
    specialEventsMenuEnabled: !!data.specialEventsMenuEnabled,
    disabledMenuItemIds: normalizeDisabledMenuItemIds(data.disabledMenuItemIds),
    attendanceStatusEnabled: data.attendanceStatusEnabled !== false,
    quickActionHiddenIds: normalizeDisabledMenuItemIds(data.quickActionHiddenIds),
    promoteBannerMode: normalisePromoteBannerMode(data.promoteBannerMode)
  };
}

function cacheKey(eventId) {
  return `${CACHE_PREFIX}${CACHE_VERSION}:${eventId || 'default'}`;
}

function readCachedSettings(eventId) {
  try {
    const raw = localStorage.getItem(cacheKey(eventId));
    if (!raw) return null;
    return normalizeMenuVisibilitySettings(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeCachedSettings(eventId, settings) {
  try {
    localStorage.setItem(cacheKey(eventId), JSON.stringify(settings));
  } catch {
    /* storage unavailable - live settings still work for this session */
  }
}

export function useMenuVisibilitySettings() {
  const eventId = getEventId();
  const cachedSettings = useMemo(() => readCachedSettings(eventId), [eventId]);
  const [settings, setSettings] = useState(() => cachedSettings || DEFAULT_MENU_VISIBILITY_SETTINGS);
  const [ready, setReady] = useState(() => Boolean(cachedSettings));
  // live: the server has confirmed the current settings. The menu can run on a
  // cached copy; switches that must not act on an old value (Dashboard
  // attendance status, Promote & Earn banner) wait for live instead. A copy
  // from the browser's Firestore cache or a listener error never counts
  // (Codex dashboard R2), so those two stay off until the server answers.
  const [live, setLive] = useState(false);

  useEffect(() => {
    setReady(Boolean(cachedSettings));
    setLive(false);
    setSettings(cachedSettings || DEFAULT_MENU_VISIBILITY_SETTINGS);

    const unsubscribe = onSnapshot(
      eventDoc(db, 'meta', 'uiSettings'),
      // Metadata changes too, so the server's confirmation of an unchanged
      // cached copy still arrives (fromCache true -> false).
      { includeMetadataChanges: true },
      (snap) => {
        const next = normalizeMenuVisibilitySettings(snap.exists() ? snap.data() : {});
        setSettings(next);
        setReady(true);
        if (!snap.metadata.fromCache) {
          setLive(true);
          writeCachedSettings(eventId, next);
        }
      },
      (err) => {
        console.error('Menu visibility settings snapshot error', err);
        setReady(true);
        setLive(false);
      }
    );

    return unsubscribe;
  }, [cachedSettings, eventId]);

  return useMemo(() => ({ settings, ready, live }), [settings, ready, live]);
}
